import { Auth, ErrorProducts, PlatformData, Request, Response, RootInitialState } from "store/reducers/root/root.types";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// This is the CLO (cargo-load-optimizer) domain state - historically a
// hand-written classic-Redux reducer in src/redux (initialState + a
// switch-case reducer + separately-named action creators). Ported onto RTK
// createSlice for one consistent state layer with app/packingList; every
// reducer body below is a 1:1 translation of the old switch-case (Immer
// lets "state.x = payload" stand in for the old "{...state, x: payload}"),
// and every action creator keeps its original external name/signature/
// default so none of the ~25 files that dispatch these needed to change
// anything beyond the import path (see store/index.ts).
const initialState: RootInitialState = {
  data: {},
  response: null,

  options: {
    lengthUnits: "mm",
    weightUnits: "kg",
    lengthAccuracy: 5,
    remainsNear: true,
    rollPlacement: 4,
  },
  groups: [],
  containers: [],
  autoContainers: [
    {
      attr: {
        type: "20st",
      },
      spaces: [
        {
          length: 5895,
          width: 2350,
          height: 2392,
          maxWeight: 28230,
        },
      ],
    },
    {
      attr: {
        type: "40st",
      },
      spaces: [
        {
          length: 12020,
          width: 2350,
          height: 2390,
          maxWeight: 26700,
        },
      ],
    },
    {
      attr: {
        type: "40hq",
      },
      spaces: [
        {
          length: 12020,
          width: 2350,
          height: 2690,
          maxWeight: 26460,
        },
      ],
    },
  ],
  auth: {
    demo: false,
    user: false,
  },

  errorProducts: [],
  palletCheckLoader: false,
  autoCountContainers: [],
  hasUserToggledAutoCount: false,
};

const rootSlice = createSlice({
  name: "root",
  initialState,
  reducers: {
    setDataInternal: (state, action: PayloadAction<PlatformData>) => {
      state.data = action.payload;
    },
    setOptionsInternal: (state, action: PayloadAction<RootInitialState["options"]>) => {
      state.options = action.payload;
    },
    setProductInternal: (state, action: PayloadAction<RootInitialState["groups"]>) => {
      state.groups = action.payload;
    },
    setGroupInternal: (state, action: PayloadAction<RootInitialState["groups"]>) => {
      state.groups = action.payload;
    },
    setTransportInternal: (state, action: PayloadAction<RootInitialState["containers"]>) => {
      state.containers = action.payload;
    },
    getResponseInternal: (state, action: PayloadAction<Response | null>) => {
      state.response = action.payload;
    },
    setAutoContainersInternal: (state, action: PayloadAction<Request["autoContainers"]>) => {
      state.autoContainers = action.payload;
    },
    setAuthInternal: (state, action: PayloadAction<Auth>) => {
      state.auth = action.payload;
    },
    setErrorProductInternal: (state, action: PayloadAction<ErrorProducts[]>) => {
      state.errorProducts = action.payload;
    },
    setPalletCheckLoaderInternal: (state, action: PayloadAction<boolean>) => {
      state.palletCheckLoader = action.payload;
    },
    setAutoCountContainersInternal: (state, action: PayloadAction<Request["autoContainers"]>) => {
      state.autoCountContainers = action.payload;
    },
    setHasUserToggledAutoCountInternal: (state, action: PayloadAction<boolean>) => {
      state.hasUserToggledAutoCount = action.payload;
    },
  },
});

const a = rootSlice.actions;

// Same external names, same parameter shapes/defaults as the old
// src/redux/actionCreators/rootActions.ts - only the "type" string each one
// dispatches changed (RTK-generated "root/setXInternal" instead of the old
// "ROOT/SET_X"), which nothing outside this reducer inspected.
export const setPlatform = (options: PlatformData) => a.setDataInternal(options);
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches the original untyped signature
export const setProduct = (data: any[] = []) => a.setProductInternal(data);
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches the original untyped signature
export const setGroup = (data: any[] = []) => a.setGroupInternal(data);
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches the original untyped signature
export const setOptions = (data: any) => a.setOptionsInternal(data);
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches the original untyped signature
export const setTransport = (data: any) => a.setTransportInternal(data);
export const getResponse = (data: Response | null) => a.getResponseInternal(data);
export const setAutoContainersAction = (data: Request["autoContainers"]) => a.setAutoContainersInternal(data);
export const setAuth = (data: Auth) => a.setAuthInternal(data);
export const setErrorProduct = (data: ErrorProducts[]) => a.setErrorProductInternal(data);
export const setPalletCheckLoader = (data: boolean) => a.setPalletCheckLoaderInternal(data);
export const setAutoCountContainersAction = (data: Request["autoContainers"]) => a.setAutoCountContainersInternal(data);
export const resetAutoCountContainersAction = () => a.setAutoCountContainersInternal([]);
export const setHasUserToggledAutoCountAction = () => a.setHasUserToggledAutoCountInternal(true);

export default rootSlice.reducer;
